import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LinkIcon from '@mui/icons-material/Link';
import PersonIcon from '@mui/icons-material/Person';
import SendIcon from '@mui/icons-material/Send';
import {
  Avatar,
  Box,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { getUiLanguage, tr } from '../lib/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { api } from '../api/client';
import type { Note, OrgUser } from '../api/types';
import { EMPLOYEE_TYPE, fmtDate, PROPERTY_STATUS, PROPERTY_STATUS_COLOR, REQUEST_STATUS, REQUEST_STATUS_COLOR, SOURCE } from '../lib/labels';
import { Button } from './button';
import { Empty, Loading } from './common';
import { MenuItem, Select } from './select';
import { TextField } from './text-field';
import { useToast } from './Toast';

export function PropertyStatusChip({ status }: { status: string }) {
  return <Chip size="small" label={PROPERTY_STATUS[status] ?? status} color={PROPERTY_STATUS_COLOR[status] ?? 'default'} />;
}
export function RequestStatusChip({ status }: { status: string }) {
  return <Chip size="small" label={REQUEST_STATUS[status] ?? status} color={REQUEST_STATUS_COLOR[status] ?? 'default'} />;
}
export function SourceChip({ source }: { source: string }) {
  return <Chip size="small" variant="outlined" icon={source === 'PUBLIC_LINK' ? <LinkIcon /> : <PersonIcon />} label={SOURCE[source] ?? source} color={source === 'PUBLIC_LINK' ? 'secondary' : 'default'} />;
}

/** Consistent identity and action header for record detail pages. */
export function DetailHeader({
  title,
  meta,
  badges,
  actions,
  onBack,
}: {
  title: ReactNode;
  meta?: ReactNode;
  badges?: ReactNode;
  actions?: ReactNode;
  onBack: () => void;
}) {
  return (
    <Box
      component="header"
      sx={(theme) => ({
        mb: 2.5,
        p: { xs: 1.5, sm: 2 },
        border: 1,
        borderColor: 'divider',
        borderRadius: 3,
        bgcolor: theme.palette.m3.surfaceContainerLow,
      })}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={2}>
        <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ minWidth: 0 }}>
          <Tooltip title={tr('رجوع', 'Back')}>
            <IconButton onClick={onBack} aria-label={tr('رجوع', 'Back')} sx={{ mt: -0.5, flexShrink: 0 }}>
              {getUiLanguage() === 'ar' ? <ArrowForwardIcon /> : <ArrowBackIcon />}
            </IconButton>
          </Tooltip>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography component="h1" variant="h5" sx={{ fontWeight: 600 }}>
                {title}
              </Typography>
              {badges}
            </Stack>
            {meta && (
              <Typography component="div" variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {meta}
              </Typography>
            )}
          </Box>
        </Stack>
        {actions && (
          <Stack
            direction="row"
            spacing={1}
            sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', md: 'center' }, '& .MuiButton-root:first-of-type': { flex: { xs: 1, md: 'initial' } } }}
          >
            {actions}
          </Stack>
        )}
      </Stack>
    </Box>
  );
}

/** label / value row for detail cards */
export function InfoRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ py: 0.75, borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>{label}</Typography>
      <Typography variant="body2" component="div" sx={{ textAlign: 'end', fontWeight: 500 }}>{children ?? '—'}</Typography>
    </Stack>
  );
}

export function SectionCard({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <Card>
      <CardHeader title={title} action={action} titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700 }} sx={{ pb: 0 }} />
      <CardContent>{children}</CardContent>
    </Card>
  );
}

/** Active employees — for assignment pickers. Needs users.view / users.manage / requests.assign. */
export function useActiveUsers(enabled = true) {
  return useQuery({
    queryKey: ['users', 'active'],
    queryFn: () => api<{ items: OrgUser[] }>('/users', { query: { status: 'ACTIVE' } }),
    enabled,
    staleTime: 60_000,
  });
}

export function AssignDialog(p: {
  open: boolean;
  title: string;
  currentUserId?: string | null;
  busy?: boolean;
  onClose: () => void;
  onSubmit: (toUserId: string | null, note: string) => void;
}) {
  const users = useActiveUsers(p.open);
  const [to, setTo] = useState<string>(p.currentUserId ?? '');
  const [note, setNote] = useState('');
  return (
    <Dialog open={p.open} onClose={p.onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{p.title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Select label={tr('الموظف', 'Employee')} value={to} onChange={(e) => setTo(e.target.value)} fullWidth>
            <MenuItem value="">— {tr('بدون تعيين', 'Unassigned')} —</MenuItem>
            {(users.data?.items ?? []).map((u) => (
              <MenuItem key={u.id} value={u.id}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                  <Avatar sx={{ width: 26, height: 26, fontSize: 13 }}>{u.full_name[0]}</Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2">{u.full_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{EMPLOYEE_TYPE[u.employee_type]} · {u.open_requests} {tr('طلب مفتوح', 'open requests')}</Typography>
                  </Box>
                </Stack>
              </MenuItem>
            ))}
          </Select>
          <TextField label={tr('ملاحظة (اختياري)', 'Note (optional)')} value={note} onChange={(e) => setNote(e.target.value)} multiline minRows={2} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={p.onClose}>{tr('إلغاء', 'Cancel')}</Button>
        <Button variant="contained" loading={p.busy} onClick={() => p.onSubmit(to || null, note.trim())}>{tr('تعيين', 'Assign')}</Button>
      </DialogActions>
    </Dialog>
  );
}

/** Notes timeline on a property / request (spec §5). */
export function NotesCard({ entityType, entityId }: { entityType: 'PROPERTY' | 'REQUEST'; entityId: string }) {
  const qc = useQueryClient();
  const { toastError } = useToast();
  const [body, setBody] = useState('');
  const key = ['notes', entityType, entityId];
  const notes = useQuery({ queryKey: key, queryFn: () => api<{ items: Note[] }>('/notes', { query: { entityType, entityId } }) });
  const add = useMutation({
    mutationFn: () => api('/notes', { method: 'POST', body: { entityType, entityId, body: body.trim() } }),
    onSuccess: () => {
      setBody('');
      void qc.invalidateQueries({ queryKey: key });
      void qc.invalidateQueries({ queryKey: [entityType === 'REQUEST' ? 'request' : 'property', entityId] });
    },
    onError: toastError,
  });
  return (
    <SectionCard title={`${tr('الملاحظات', 'Notes')}${notes.data ? ` (${notes.data.items.length})` : ''}`}>
      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 2 }}>
        <TextField
          fullWidth
          multiline
          maxRows={6}
          label={tr('أضف ملاحظة أو نتيجة تواصل', 'Add a note or contact outcome')}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && body.trim()) add.mutate();
          }}
        />
        <Tooltip title={tr('إضافة (Ctrl+Enter)', 'Add (Ctrl+Enter)')}>
          <span>
            <IconButton color="primary" disabled={!body.trim() || add.isPending} onClick={() => add.mutate()} sx={{ mt: 1 }} aria-label={tr('إضافة ملاحظة', 'Add note')}>
              {add.isPending
                ? <CircularProgress size="30px" aria-label={tr('جارٍ التحميل…', 'Loading…')} />
                : <SendIcon sx={{ transform: getUiLanguage() === 'ar' ? 'scaleX(-1)' : undefined }} />}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      {notes.isLoading ? <Loading size="30px" /> : !notes.data?.items.length ? <Empty text={tr('لا توجد ملاحظات بعد', 'No notes yet')} /> : (
        <Stack spacing={1.5}>
          {notes.data.items.map((n) => (
            <Stack key={n.id} direction="row" spacing={1.5}>
              <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>{n.author_name[0]}</Avatar>
              <Box sx={{ flex: 1, bgcolor: 'action.hover', borderRadius: 2, px: 1.5, py: 1 }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" fontWeight={600}>{n.author_name}</Typography>
                  <Typography variant="caption" color="text.secondary">{fmtDate(n.created_at)}</Typography>
                </Stack>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{n.body}</Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
      )}
    </SectionCard>
  );
}
